// Webhook endpoint detail (task [3.6], ws-gp3r9) — the endpoint identity block
// (composed on the shared ItemDetailPage primitive) plus the master-detail
// Events body. This is the FORMER single `/webhooks` screen, moved verbatim to
// `/webhooks/:id`; the index page ([3.6]) now owns `/webhooks` and routes here.
//
// The clicked config is handed across the navigation in `location.state` so the
// drill-down is stable in mock mode; a direct/refreshed nav (no state) falls
// back to looking the config up by :id from the same useWebhookConfigs() list
// the index already loads — no new fetcher, mirroring delivery-log/[id].
//
// No new relative-time util exists in the repo (delivery-log / retry-processor
// format ABSOLUTE times via Intl.DateTimeFormat), so CREATED gets a small inline
// relative formatter here rather than a reinvented shared helper.
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
	AlertCircle,
	ArrowLeft,
	Check,
	Copy,
	Eye,
	EyeOff,
	Webhook,
} from 'lucide-react';
import { Link, useLocation, useParams } from 'react-router';

import { useWebhookConfigs, useWebhookEvents } from '@/api/webhooks/hooks';
import type { WebhookConfig } from '@/api/webhooks/mock';
import {
	ItemDetailPage,
	type ItemDetailMetadata,
} from '@/components/layout/item-detail-page';
import { PageContainer } from '@/components/layout/page-container';
import { Badge } from '@/components/ui/badge';
import { EventDetail } from '../_components/event-detail';
import { EventList } from '../_components/event-list';

/** How many event-type chips to show inline before collapsing to a `+N` count. */
const MAX_VISIBLE_EVENTS = 3;

const relativeFmt = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' });

/** Coarse "N units ago" from an ISO timestamp — display only. */
function formatRelative(iso: string): string {
	const then = new Date(iso).getTime();
	if (Number.isNaN(then)) return '—';
	const deltaSec = Math.round((then - Date.now()) / 1000);
	const abs = Math.abs(deltaSec);
	const steps: [Intl.RelativeTimeFormatUnit, number][] = [
		['year', 60 * 60 * 24 * 365],
		['month', 60 * 60 * 24 * 30],
		['day', 60 * 60 * 24],
		['hour', 60 * 60],
		['minute', 60],
	];
	for (const [unit, secs] of steps) {
		if (abs >= secs) return relativeFmt.format(Math.round(deltaSec / secs), unit);
	}
	return relativeFmt.format(deltaSec, 'second');
}

/** Inline event-type chips with a `+N` overflow count (e.g. `email.sent … +15`). */
function ListeningForChips({ events }: { events: WebhookConfig['listeningFor'] }) {
	if (events.length === 0) {
		return <span className="text-muted-foreground">Nothing</span>;
	}

	const visible = events.slice(0, MAX_VISIBLE_EVENTS);
	const overflow = events.length - visible.length;

	return (
		<div className="flex flex-wrap items-center gap-1.5">
			{visible.map((event) => (
				<Badge key={event} status="neutral">
					{event}
				</Badge>
			))}
			{overflow > 0 ? <Badge status="info">+{overflow}</Badge> : null}
		</div>
	);
}

/**
 * Masked signing-secret value with a reveal toggle and a copy button. Reveal is
 * local `useState`; the copy button mirrors the visual / interaction pattern
 * CodePanel's own copy button established (Copy → Check).
 */
function SigningSecret({ secret }: { secret: string }) {
	const [revealed, setRevealed] = useState(false);
	const [copied, setCopied] = useState(false);

	const handleCopy = useCallback(() => {
		void navigator.clipboard.writeText(secret).then(() => {
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		});
	}, [secret]);

	return (
		<div className="flex items-center gap-2">
			<code className="min-w-0 truncate font-mono text-xs text-foreground">
				{revealed ? secret : '•'.repeat(16)}
			</code>
			<button
				type="button"
				onClick={() => setRevealed((v) => !v)}
				className="inline-flex shrink-0 items-center border border-border-strong p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
				aria-label={revealed ? 'Hide signing secret' : 'Reveal signing secret'}
			>
				{revealed ? (
					<EyeOff className="size-3.5" aria-hidden="true" />
				) : (
					<Eye className="size-3.5" aria-hidden="true" />
				)}
			</button>
			<button
				type="button"
				onClick={handleCopy}
				className="inline-flex shrink-0 items-center border border-border-strong p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
				aria-label={copied ? 'Copied' : 'Copy signing secret'}
			>
				{copied ? (
					<Check className="size-3.5 text-success" aria-hidden="true" />
				) : (
					<Copy className="size-3.5" aria-hidden="true" />
				)}
			</button>
		</div>
	);
}

function BackToWebhooks() {
	return (
		<Link
			to="/webhooks"
			viewTransition
			className="inline-flex items-center gap-1.5 border border-border-strong px-2.5 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
		>
			<ArrowLeft className="size-4" aria-hidden="true" />
			Back to webhooks
		</Link>
	);
}

function WebhooksSkeleton() {
	return (
		<PageContainer>
			<div className="flex items-center gap-4">
				<div className="size-10 shrink-0 animate-pulse rounded-md bg-muted" />
				<div className="h-10 w-80 animate-pulse rounded-md bg-muted" />
			</div>
			<div className="grid grid-cols-2 gap-4 md:grid-cols-3">
				{Array.from({ length: 4 }).map((_, i) => (
					<div key={i} className="h-12 animate-pulse rounded-md bg-muted" />
				))}
			</div>
		</PageContainer>
	);
}

function WebhooksError({ onRetry }: { onRetry: () => void }) {
	return (
		<PageContainer>
			<div className="flex flex-col items-center gap-4 py-16 text-center">
				<p className="text-sm text-muted-foreground">
					Couldn’t load the webhook endpoint.
				</p>
				<button
					type="button"
					onClick={onRetry}
					className="border border-border-strong px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
				>
					Try again
				</button>
			</div>
		</PageContainer>
	);
}

function WebhooksNotFound() {
	return (
		<PageContainer>
			<div className="flex flex-col items-center gap-4 py-16 text-center">
				<div className="flex size-10 items-center justify-center rounded-md bg-muted text-muted-foreground">
					<Webhook className="size-5" aria-hidden="true" />
				</div>
				<div>
					<h1 className="font-serif text-xl font-bold text-foreground">
						Endpoint not found
					</h1>
					<p className="mt-1 text-sm text-muted-foreground">
						No webhook endpoint matches this id.
					</p>
				</div>
				<BackToWebhooks />
			</div>
		</PageContainer>
	);
}

/** Skeleton for the two-pane events body while the first page loads. */
function EventsBodySkeleton() {
	return (
		<div className="grid gap-4 lg:grid-cols-[minmax(0,20rem)_1fr]">
			<div className="h-[34rem] animate-pulse rounded-lg bg-muted" />
			<div className="h-[34rem] animate-pulse rounded-lg bg-muted" />
		</div>
	);
}

/**
 * Two-pane master-detail Events body. Owns the events infinite query AND the
 * selected-event id — a plain client-side `useState`, so selecting a row swaps
 * the detail pane with NO route/URL change. Defaults the selection to the first
 * event once the first page arrives.
 *
 * Replay lives in EventDetail and invalidates the events query on success; this
 * component re-derives `selected` from the freshly-refetched `events`, so the
 * incremented attempt count / refreshed status flow into BOTH panes automatically.
 */
function EventsBody() {
	const {
		data,
		isLoading,
		isError,
		refetch,
		hasNextPage,
		isFetchingNextPage,
		fetchNextPage,
	} = useWebhookEvents();

	const [selectedId, setSelectedId] = useState<string | null>(null);

	const events = useMemo(
		() => data?.pages.flatMap((p) => p.events) ?? [],
		[data],
	);

	// Default-select the first event once data arrives; leaves an explicit user
	// selection untouched thereafter (only fills a still-null selection).
	useEffect(() => {
		if (selectedId === null && events.length > 0) {
			setSelectedId(events[0].id);
		}
	}, [selectedId, events]);

	const onLoadMore = useCallback(() => void fetchNextPage(), [fetchNextPage]);

	if (isLoading) return <EventsBodySkeleton />;

	if (isError) {
		return (
			<div className="flex flex-col items-center gap-4 rounded-lg border border-border py-16 text-center">
				<AlertCircle className="size-10 text-destructive" aria-hidden="true" />
				<p className="text-sm text-muted-foreground">
					Couldn’t load webhook events.
				</p>
				<button
					type="button"
					onClick={() => void refetch()}
					className="border border-border-strong px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
				>
					Try again
				</button>
			</div>
		);
	}

	if (events.length === 0) {
		return (
			<div className="rounded-lg border border-dashed border-border py-16 text-center text-sm text-muted-foreground">
				No events delivered yet.
			</div>
		);
	}

	const selected = events.find((e) => e.id === selectedId) ?? null;

	return (
		<section className="flex flex-col gap-3">
			<h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
				Events
			</h2>
			<div className="grid gap-4 lg:grid-cols-[minmax(0,20rem)_1fr]">
				<EventList
					events={events}
					selectedId={selectedId}
					onSelect={setSelectedId}
					hasNextPage={hasNextPage}
					isFetchingNextPage={isFetchingNextPage}
					onLoadMore={onLoadMore}
				/>
				<EventDetail event={selected} />
			</div>
		</section>
	);
}

export default function WebhookDetailPage() {
	const { id } = useParams<{ id: string }>();
	const location = useLocation();
	// The index hands the clicked config across the navigation so the drill-down
	// is stable without a new single-fetch seam. A direct/refreshed nav (no state)
	// falls back to the useWebhookConfigs() list lookup, which also drives the
	// loading/error/not-found states below.
	const passedConfig = (location.state as { config?: WebhookConfig } | null)
		?.config;
	const { data, isLoading, isError, refetch } = useWebhookConfigs();

	const config =
		passedConfig ??
		data?.pages.flatMap((p) => p.configs).find((c) => c.id === id);

	if (!config && isLoading) return <WebhooksSkeleton />;
	if (!config && isError)
		return <WebhooksError onRetry={() => void refetch()} />;
	if (!config) return <WebhooksNotFound />;

	const metadata: ItemDetailMetadata[] = [
		{
			label: 'Listening For',
			value: <ListeningForChips events={config.listeningFor} />,
		},
		{
			label: 'Status',
			value: (
				<Badge status={config.status === 'enabled' ? 'success' : 'neutral'}>
					{config.status === 'enabled' ? 'Enabled' : 'Disabled'}
				</Badge>
			),
		},
		{
			label: 'Created',
			value: (
				<span suppressHydrationWarning>{formatRelative(config.createdAt)}</span>
			),
		},
		{
			label: 'Signing Secret',
			value: <SigningSecret secret={config.signingSecret} />,
		},
	];

	return (
		<ItemDetailPage
			icon={<Webhook aria-hidden="true" />}
			eyebrow="Webhook"
			title={config.url}
			actions={<BackToWebhooks />}
			metadata={metadata}
		>
			<EventsBody />
		</ItemDetailPage>
	);
}
